import { Component, DestroyRef, HostBinding, inject, input, InputSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, Observable, of, switchMap, take, timer } from 'rxjs';
import { VersionService } from '../services/version.service';

type VersionLabelMode = 'interactive' | 'passive' | 'display-only' | 'inline';

const RELOAD_COUNTDOWN_SECONDS = 30;
const INACTIVITY_PERIOD_MS = 20 * 60 * 1000;

@Component({
    selector: 'app-version-label',
    templateUrl: './version-label.component.html',
    styleUrls: ['./version-label.component.scss'],
    standalone: true
})
export class VersionLabelComponent implements OnInit {

    private versionService = inject(VersionService);
    private destroyRef = inject(DestroyRef);

    public mode: InputSignal<VersionLabelMode> = input<VersionLabelMode>('interactive');

    public isReloadCandidate = toSignal(this.versionService.isReloadCandidate(), { initialValue: false });
    public shouldReloadNow = toSignal(this.versionService.shouldReloadNow(), { initialValue: false });
    public currentVersion = this.versionService.getCurrentVersion();
    public countdownSeconds = RELOAD_COUNTDOWN_SECONDS;
    public showPassiveCountdown = false;

    @HostBinding('class.version-label--passive')
    get isPassive(): boolean {
        return this.mode() === 'passive' || this.mode() === 'display-only';
    }

    @HostBinding('class.version-label--countdown-active')
    get isCountdownActive(): boolean {
        return this.showPassiveCountdown;
    }

    ngOnInit(): void {
        if (this.mode() === 'passive') {
            this.listenAutoRefresh();
        } else if (this.mode() === 'interactive') {
            this.listenReloadCountdown();
        }
    }

    updateNow(): void {
        this.versionService.forceRefresh();
    }

    private listenReloadCountdown(): void {
        this.versionService.shouldReloadNow()
            .pipe(
                switchMap(shouldReload => {
                    if (!shouldReload) {
                        return of(RELOAD_COUNTDOWN_SECONDS);
                    }
                    return this.buildCountdownTimer();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(secondsRemaining => {
                this.countdownSeconds = secondsRemaining;

                if (secondsRemaining <= 0) {
                    this.versionService.forceRefresh();
                }
            });
    }

    private listenAutoRefresh(): void {
        combineLatest([
            this.versionService.isReloadCandidate(),
            this.versionService.shouldReloadNow()
        ])
            .pipe(
                filter(([isReloadCandidate]) =>
                    isReloadCandidate
                ),
                switchMap(([, shouldReloadNow]) => {
                    this.showPassiveCountdown = false;
                    const totalDelayMs = shouldReloadNow
                        ? RELOAD_COUNTDOWN_SECONDS * 1000
                        : INACTIVITY_PERIOD_MS;
                    const silentWaitMs = Math.max(totalDelayMs - RELOAD_COUNTDOWN_SECONDS * 1000, 0);

                    return timer(silentWaitMs)
                        .pipe(
                            switchMap(() => {
                                this.showPassiveCountdown = true;
                                return this.buildCountdownTimer();
                            })
                        );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(secondsRemaining => {
                this.countdownSeconds = secondsRemaining;

                if (secondsRemaining <= 0) {
                    const refreshed = this.versionService.attemptRefresh();

                    if (!refreshed) {
                        this.showPassiveCountdown = false;
                        this.countdownSeconds = RELOAD_COUNTDOWN_SECONDS;
                    }
                }
            });
    }

    private buildCountdownTimer(): Observable<number> {
        return timer(0, 1000)
            .pipe(
                take(RELOAD_COUNTDOWN_SECONDS),
                map(tick =>
                    RELOAD_COUNTDOWN_SECONDS - 1 - tick
                )
            );
    }
}
